import { ApiService } from '../../services/api_service';
import { ContextService } from '../../services/context_service';
import { DatenauswertungGenerisch } from './DatenauswertungGenerisch';
import { DatenauswertungJahrauswahlEinzeln } from './DatenauswertungJahrauswahlEinzeln';
import { DatenauswertungReportKonfigurationBemerkungButton } from './DatenauswertungReportKonfigurationBemerkungButton';
import { fn2 } from '../../core/formatter/formatNumber';
import { getCO2Scope3StructureAllgemein, getCO2Scope3StructureNachgelagert, getCO2Scope3StructureVorgelagert } from '../dateneingabe/functions/getCO2Scope3Structure';
import { getRoutingCO2Scope3 } from '../../helpers/getRoutingCO2Scope3';
import { IDs } from '../../helpers/IDs';
import { lazyInject } from '../../services/inversify';
import * as Moment from 'moment';
import * as React from 'react';
import { rowHasValues } from './generisch/renderRows';
import { Validierung } from './elements/Validierung';
import { ValidierungHinweise } from './elements/ValidierungHinweise';
import { WActionButton } from '../../core/elements/WActionButton';
import { WHelpIcon } from '../../core/elements/WHelpIcon';

interface DatenauswertungCO2THGStandorteProps extends React.ClassAttributes<any> {
    data: any;
    routing: any;
    onRoutingChange: (routing: any) => void;
    jahrFirst?: number;
    jahrLast?: number;
    setJSXdirect?: (content: JSX.Element) => void;
    callbackPages?: (pageNumber: number, totalPages: number) => void;
}

interface DatenauswertungCO2THGStandorteState {
    pdfString?: string;
    jahr: number;
    jahrMin: number;
    jahrMax: number;
    renderButton: JSX.Element;
    validation: any;
    chartKey: string;
    standorteSortIndex: number;
}

export class DatenauswertungCO2THGStandorte extends React.Component<DatenauswertungCO2THGStandorteProps, DatenauswertungCO2THGStandorteState> {

    constructor(props: any) {
        super(props);

        const jahrMax = Moment().year();
        let jahrMin = 2010;
        const konfig = props.data[IDs.ENT_KONFIGURATION];
        if (konfig && konfig[0] && konfig[0].startjahr_datenerfassung && konfig[0].startjahr_datenerfassung > 1900) {
            jahrMin = konfig[0].startjahr_datenerfassung;
        }
        const jahrContext = this.contextService.get('jahr_cockpit');
        const jahrUse = props.jahr || (jahrContext && jahrContext <= jahrMax ? jahrContext : jahrMax);

        this.state = {
            jahr: jahrUse,
            jahrMin: jahrMin,
            jahrMax: jahrMax,
            renderButton: undefined,
            validation: null,
            chartKey: 'Gesamt',
            standorteSortIndex: null
        };
    }

    @lazyInject(ApiService) private apiService: ApiService;
    @lazyInject(ContextService) private contextService: ContextService;

    private entities: any[] = [];
    private chartColors = ['#4f81bd', '#f79646', '#87bd51', '#51bd75', '#5163bd', '#9951bd', '#bd5175', '#bd5151'];
    private standorte: any[];
    private standorteSortOptions = [
        { name: 'Scope 1, aufsteigend', sort: (a: any, b: any) => a.co2_fussabdruck_scope1 - b.co2_fussabdruck_scope1 },
        { name: 'Scope 1, absteigend', sort: (a: any, b: any) => b.co2_fussabdruck_scope1 - a.co2_fussabdruck_scope1 },
        { name: 'Scope 2, aufsteigend', sort: (a: any, b: any) => a.co2_fussabdruck_scope2 - b.co2_fussabdruck_scope2 },
        { name: 'Scope 2, absteigend', sort: (a: any, b: any) => b.co2_fussabdruck_scope2 - a.co2_fussabdruck_scope2 },
        { name: 'Scope 3, aufsteigend', sort: (a: any, b: any) => a.co2_fussabdruck_scope3 - b.co2_fussabdruck_scope3 },
        { name: 'Scope 3, absteigend', sort: (a: any, b: any) => b.co2_fussabdruck_scope3 - a.co2_fussabdruck_scope3 },
        { name: 'Gesamt, aufsteigend', sort: (a: any, b: any) => a.co2_fussabdruck_total - b.co2_fussabdruck_total },
        { name: 'Gesamt, absteigend', sort: (a: any, b: any) => b.co2_fussabdruck_total - a.co2_fussabdruck_total },
    ];

    componentDidMount(): void {
        const bereichKey = this.apiService.getBereichKey();
        const modul: string = (this.props.data || {}).MODUL_SELECTED || null;
        this.standorte = (this.props.data[IDs.ENT_STANDORT] || []).filter((s: any) => s.energiedaten_erfassen_orig === 1);
        [
            ...getCO2Scope3StructureVorgelagert(bereichKey, modul) || [],
            ...getCO2Scope3StructureNachgelagert(bereichKey, modul) || [],
            ...getCO2Scope3StructureAllgemein() || []
        ].forEach((entity: any) => {
            if (!entity.ignore && entity.key && entity.name) {
                this.entities.push({ titel: entity.name, entity: `co2_scope3_${entity.key}` });
            }
        });
        const validation: any = {};
        this.standorte.forEach((item: any) => {
            validation[item.tid] = new Validierung(
                this.props.data,
                item.tid,
                this.entities,
                'jahr'
            ).getData()
        });
        this.setState({ validation });
    }

    getValues(): any {
        const { jahr, standorteSortIndex } = this.state;
        const konfiguration: any[] = this.props.data[IDs.ENT_KONFIGURATION] || [];
        const disabledCO2Komplex: boolean = !IDs.FLAG_ENABLE_SCOPE3
            || !konfiguration.some((param: any) => param.co2_fussabdruck_orig === 1);
        const rows: any[] = [];
        const chartData: any[] = [];
        const custom_row_classes: any[] = [];
        const row_web_forced: boolean[] = [];
        const DATA_BERECHNET_STANDORT = this.props.data[IDs.KENNZIFFERN_BERECHNET_STANDORT] || {};
        const kz_gesamt: any = (this.props.data[IDs.KENNZIFFERN_BERECHNET] || {})[jahr] || {};

        const sortOption = this.standorteSortOptions[standorteSortIndex];
        if (sortOption) {
            this.standorte.sort((a: any, b: any) => {
                const kza: any = (DATA_BERECHNET_STANDORT[a.tid] || {})[jahr] || {};
                const kzb: any = (DATA_BERECHNET_STANDORT[b.tid] || {})[jahr] || {};
                return sortOption.sort(kza, kzb);
            });
        } else {
            this.standorte.sort((a: any, b: any) =>
                a.standort_typ_tid_orig !== b.standort_typ_tid_orig ?
                    a.standort_typ_tid_orig - b.standort_typ_tid_orig
                    : a.name > b.name ? 1 : a.name < b.name ? -1 : 1
            );
        }

        this.standorte.forEach((standort: any) => {
            const kz: any = (DATA_BERECHNET_STANDORT[standort.tid] || {})[jahr] || {};
            const row = [
                `${standort.name}`,
                kz.co2_fussabdruck_scope1 > 0 ? fn2(kz.co2_fussabdruck_scope1) : '',
                kz.co2_fussabdruck_scope1 > 0 && kz_gesamt.co2_fussabdruck_scope1 > 0 ? fn2(kz.co2_fussabdruck_scope1 / kz_gesamt.co2_fussabdruck_scope1 * 100) : '',
                kz.co2_fussabdruck_scope2 > 0 ? fn2(kz.co2_fussabdruck_scope2) : '',
                kz.co2_fussabdruck_scope2 > 0 && kz_gesamt.co2_fussabdruck_scope2 > 0 ? fn2(kz.co2_fussabdruck_scope2 / kz_gesamt.co2_fussabdruck_scope2 * 100) : '',
                kz.co2_fussabdruck_scope3 > 0 ? fn2(kz.co2_fussabdruck_scope3) : '',
                kz.co2_fussabdruck_scope3 > 0 && kz_gesamt.co2_fussabdruck_scope3 > 0 ? fn2(kz.co2_fussabdruck_scope3 / kz_gesamt.co2_fussabdruck_scope3 * 100) : '',
                kz.co2_fussabdruck_total > 0 ? fn2(kz.co2_fussabdruck_total) : '',
                kz.co2_fussabdruck_total > 0 && kz_gesamt.co2_fussabdruck_total > 0 ? fn2(kz.co2_fussabdruck_total / kz_gesamt.co2_fussabdruck_total * 100) : ''
            ];
            if (disabledCO2Komplex) {
                row.splice(5, 2); // Entferne Scope3
            }
            const row_length: number = row.length;
            const hasValues: boolean = rowHasValues(row);
            const isWebForced: boolean = !!(!hasValues && this.state.validation[jahr]);
            custom_row_classes.push(isWebForced ? Array.from(Array(row_length), () => 'row_field') : undefined);
            row_web_forced.push(isWebForced);
            rows.push([
                <div data-text={row[0]}>
                    <div>{row[0]}</div>
                    {(this.state.validation[standort.tid] || {})[jahr] &&
                        <ValidierungHinweise
                            entities={this.entities}
                            id={jahr}
                            showPriorityColors
                            validation={this.state.validation[standort.tid]}
                        />
                    }
                </div>,
                ...(!hasValues && (this.state.validation[standort.tid] || {})[jahr] ?
                    Array.from(Array(row_length - 1))
                    : [...Array(row_length - 1)].map((item: any, index: number) => row[index + 1])
                )
            ]);
        });
        rows.push([
            'Gesamt',
            kz_gesamt.co2_fussabdruck_scope1 > 0 ? fn2(kz_gesamt.co2_fussabdruck_scope1) : '',
            kz_gesamt.co2_fussabdruck_scope1 > 0 ? fn2(100) : '',
            kz_gesamt.co2_fussabdruck_scope2 > 0 ? fn2(kz_gesamt.co2_fussabdruck_scope2) : '',
            kz_gesamt.co2_fussabdruck_scope2 > 0 ? fn2(100) : '',
            kz_gesamt.co2_fussabdruck_scope3 > 0 ? fn2(kz_gesamt.co2_fussabdruck_scope3) : '',
            kz_gesamt.co2_fussabdruck_scope3 > 0 ? fn2(100) : '',
            kz_gesamt.co2_fussabdruck_total > 0 ? fn2(kz_gesamt.co2_fussabdruck_total) : '',
            kz_gesamt.co2_fussabdruck_total > 0 ? fn2(100) : ''
        ]);

        Object.keys(DATA_BERECHNET_STANDORT)
            .sort((a: string, b: string) => ((DATA_BERECHNET_STANDORT[b][jahr] || {}).co2_fussabdruck_total || 0) - ((DATA_BERECHNET_STANDORT[a][jahr] || {}).co2_fussabdruck_total || 0))
            .forEach((sTid: string, i: number) => {
                const kz: any = (DATA_BERECHNET_STANDORT[sTid] || {})[jahr] || {};
                const standortFnd = this.standorte.find((s: any) => s.tid === +sTid);
                if (standortFnd) {
                    let value: number;
                    let percentage: number;
                    switch (this.state.chartKey) {
                        case 'Scope 1':
                            value = kz.co2_fussabdruck_scope1;
                            percentage = value / kz_gesamt.co2_fussabdruck_scope1 * 100;
                            break;
                        case 'Scope 2':
                            value = kz.co2_fussabdruck_scope2;
                            percentage = value / kz_gesamt.co2_fussabdruck_scope2 * 100;
                            break;
                        case 'Scope 3':
                            value = kz.co2_fussabdruck_scope3;
                            percentage = value / kz_gesamt.co2_fussabdruck_scope3 * 100;
                            break;
                        default:
                            value = kz.co2_fussabdruck_total;
                            percentage = value / kz_gesamt.co2_fussabdruck_total * 100;
                    }
                    if (value > 0) {
                        const legend = `${standortFnd.name} [${fn2(value)} t CO₂ Äq / ${fn2(percentage)} %]`;
                        chartData.push({
                            key: standortFnd.tid, name: `${standortFnd.name}`, value, percentage, legend,
                            color: this.chartColors[i % this.chartColors.length]
                        });
                    }
                }
            });

        return { rows, chartData, custom_row_classes, row_web_forced };
    }

    render(): JSX.Element {
        if (!this.state.validation) {
            return null;
        }
        const konfiguration: any[] = this.props.data[IDs.ENT_KONFIGURATION] || [];
        const disabledCO2Komplex: boolean = !IDs.FLAG_ENABLE_SCOPE3
            || !konfiguration.some((param: any) => param.co2_fussabdruck_orig === 1);
        const numCols = !disabledCO2Komplex ? 9 : 7;
        const baseWidth = 100 / numCols; // Bitte immer in Prozent angeben
        const unternehmen: string = ((this.props.data[IDs.ENT_UNTERNEHMENSDATEN] || [])[0] || {}).name
            || (this.props.data.RES_MAIN_LOAD_UNTERNEHMEN_SEL || {}).bezeichnung || null;
        const values = this.getValues();
        const customStyle = { backgroundColor: '#728A49', color: '#fff' };
        const title = `Anteile der Standorte an der THG - Bilanz / Jahr ${this.state.jahr}`;
        const chartTitle = `${title} / ${this.state.chartKey}`;
        const header_blocks_columns = [
            {
                label: 'Absoluter Anteil', label2: 't CO₂ Äq', customStyleLabel: { ...customStyle },
                customStyleLabel2: { borderTop: '1pt solid #fff', ...customStyle }
            },
            {
                label: 'Relativer Anteil', label2: '%', customStyleLabel: { ...customStyle },
                customStyleLabel2: { borderTop: '1pt solid #fff', ...customStyle }
            },
        ];

        const config: any = {
            titel0: unternehmen,
            titel1: title,
            filename: `Anteile_der_Standorte_THG_${this.state.jahr}.pdf`,
            report: { data: (this.props.data || {})[IDs.ENT_REPORT] || [], standort: 0, key: 'co2' },
            baseWidth: baseWidth,
            header: {
                blocks: [
                    {
                        label: 'Standorte',
                        customStyle,
                        width: `${baseWidth}%`
                    },
                    {
                        label: 'SCOPE 1\n (direkte Emissionen - Energie)',
                        width: `${baseWidth * 2}%`,
                        customStyle,
                        columns: header_blocks_columns
                    },
                    {
                        label: 'SCOPE 2\n (indirekte Emissionen - Energie)',
                        width: `${baseWidth * 2}%`,
                        customStyle,
                        columns: header_blocks_columns
                    },
                    {
                        label: 'SCOPE 3\n (indirekte Emissionen - Weitere)',
                        width: `${baseWidth * 2}%`,
                        customStyle,
                        columns: header_blocks_columns
                    },
                    {
                        label: 'Emissionen\nGESAMT',
                        width: `${baseWidth * 2}%`,
                        customStyle,
                        columns: header_blocks_columns
                    }
                ]
            },
            rows: values.rows,
            row_classes: [
                'row_field_header_green2', 'row_field_green', 'row_field_green', 'row_field_green', 'row_field_green', 'row_field_green',
                'row_field_green', 'row_field_green', 'row_field_green'
            ],
            custom_row_classes: values.custom_row_classes,
            row_web_forced: values.row_web_forced,
            diagrams: [
                {
                    type: 'PieChart',
                    layout: 'row',
                    title: chartTitle,
                    titlePdf: chartTitle,
                    legend: values.chartData.map((d: any) => d.legend),
                    legendIconColor: values.chartData.map((d: any) => d.color),
                    data: values.chartData,
                    lineColor: this.chartColors,
                    containerSize: { width: '45%' /* !importent */ },
                    chartSelectable: disabledCO2Komplex ?
                        {
                            'Gesamt': ['scope_1', 'scope_2'],
                            'Scope 1': ['scope_1'],
                            'Scope 2': ['scope_2'],
                        }
                        : {
                            'Gesamt': ['scope_1', 'scope_2', 'scope_3'],
                            'Scope 1': ['scope_1'],
                            'Scope 2': ['scope_2'],
                            'Scope 3': ['scope_3']
                        },
                    selectedChartKey: this.state.chartKey,
                    diagramStyle: { height: 310 },
                    hideLabelsFilter: (data: any, rows: any[], index: number) => {
                        return (data.percentage || 0) < 2;
                    },
                    chartSelectChange: (chartKey: string) => this.setState({ chartKey })
                }
            ]
        };
        if (disabledCO2Komplex) {
            config.header.blocks.splice(3, 1); // Entferne Scope3
        }
        const helpIcon = (
            <WHelpIcon
                data={this.props.data}
                styleDivProps={{ display: 'inline-block' }}
                styleProps={{ width: 18, height: 18, borderRadius: 10, backgroundColor: '#728A49', verticalAlign: 'top', margin: 0 }}
                label='Datenauswertung - Hinweise'
                helpKey='datenauswertung_THG'
                type='info'
                editBar
            />
        );
        return (
            <div>
                {!this.props.setJSXdirect &&
                    <div style={{ flex: 1, flexDirection: 'row', display: 'flex', backgroundColor: '#E9E9E9', padding: '3px 0 3px 16px' }}>
                        <DatenauswertungJahrauswahlEinzeln
                            jahrMax={this.state.jahrMax}
                            jahrMin={this.state.jahrMin}
                            jahr={this.state.jahr}
                            externalHeaderElements={[
                                this.renderStandorteSort(),
                                this.renderEingabeButton(),
                                this.renderBemerkungButton(),
                                this.state.renderButton
                            ]}
                            onJahrChange={(jahr: number) => {
                                this.setState({ jahr: +jahr }, () => {
                                    this.contextService.set('jahr_cockpit', jahr);
                                });
                            }}
                        />
                        <div style={{ padding: 5, paddingRight: 20 }}>{helpIcon}</div>
                    </div>
                }
                <DatenauswertungGenerisch
                    config={config}
                    orientation='landscape'
                    setRenderButton={(renderButton: JSX.Element) => this.setState({ renderButton })}
                    setJSXdirect={this.props.setJSXdirect}
                    callbackPages={typeof this.props.callbackPages === 'function' ? this.props.callbackPages : null}
                    pdfButtonStyle={{ backgroundColor: '#728A49', color: '#fff' }}
                />
            </div>
        );
    }

    renderStandorteSort(): JSX.Element {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>Sortierung:</div>
                <select
                    style={{ marginLeft: 5, padding: 2 }}
                    value={this.state.standorteSortIndex}
                    onChange={(event: any) => this.setState({ standorteSortIndex: event.target.value })}
                >
                    <option value={null}>Standorttyp</option>
                    {this.standorteSortOptions.map((o: any, i: number) => <option value={i}>{o.name}</option>)}
                </select>
            </div>
        );
    }

    renderBemerkungButton(): JSX.Element {
        return (
            <DatenauswertungReportKonfigurationBemerkungButton
                data={this.props.data}
                standort={0}
                type='co2'
                styleProps={{ backgroundColor: '#728A49', color: '#fff' }}
            />
        );
    }

    renderEingabeButton(): JSX.Element {
        return (
            <WActionButton
                label='Dateneingabe'
                insideText='Dateneingabe'
                tooltip='Zum Dateneingabebereich springen'
                key='dateneingabe'
                action='edit'
                styleProps={{ backgroundColor: '#728A49', color: '#fff' }}
                onAction={() => this.props.onRoutingChange({
                    ...(getRoutingCO2Scope3(this.props.data, 0)), key: 'inputScope3'
                })}
            />
        );
    }
}
